import {Pill, PillVariant, OutlinedPillVariant} from '@forecast-it/design-system';
import type {TicketStatus} from '../types/ticket';

export const STATUS_LABELS: Record<TicketStatus, string> = {
	open: 'Open',
	in_progress: 'In progress',
	waiting_on_customer: 'Waiting on customer',
	resolved: 'Resolved',
	closed: 'Closed',
};

/**
 * Status renders as a design-system `Pill`. The five production statuses map onto
 * the variants the design system offers — filled while a ticket is still live work,
 * outlined once it has left the queue, so an active row reads louder than a settled
 * one. In-repo precedent for folding a longer status list onto these variants:
 * `StatusPill` in `src/components/ai-chat`.
 */
const STATUS_PILL: Record<TicketStatus, {filled: PillVariant} | {outlined: OutlinedPillVariant}> = {
	open: {filled: PillVariant.INFORMATION},
	in_progress: {filled: PillVariant.SUCCESS},
	waiting_on_customer: {filled: PillVariant.NEUTRAL},
	resolved: {outlined: OutlinedPillVariant.SUCCESS},
	closed: {outlined: OutlinedPillVariant.NEUTRAL},
};

export function statusPillProps(status: TicketStatus): {filled: PillVariant} | {outlined: OutlinedPillVariant} {
	return STATUS_PILL[status];
}

interface Props {
	status: TicketStatus;
}

export function TicketStatusBadge({status}: Props) {
	const config = STATUS_PILL[status];
	const label = STATUS_LABELS[status];
	// Outlined pills read `outlinedVariant`, not `variant` — passing `variant` on an
	// outlined pill silently falls back to the neutral stroke.
	return 'outlined' in config ? (
		<Pill outlined outlinedVariant={config.outlined}>
			{label}
		</Pill>
	) : (
		<Pill variant={config.filled}>{label}</Pill>
	);
}
